import { useEffect, useRef, useState } from "react";
import Header from "@/sections/Header";
import VoiceCard from "./VoiceCard";
import IC_NONE from "@/assets/record/ic_none.png";
import IC_ADD from "@/assets/record/ic_add.svg?react";
import VoiceAddModal, { VoiceAddModalRef } from "./VoiceAddModal";
import { IVoiceItem } from "@/types/voice";
import { getVoiceList } from "@/services/voice";
import { Loading, LoadMore } from "@/component/Loading";
import AudioPlayerIns from "@/component/AudioPlayer/AudioPlayerIns";
import { getSpeakerAvatarInfo } from "@/utils/newAvatar";

let page = 0; // 页码

const Voice = () => {
  const [list, setList] = useState<Array<IVoiceItem>>([]);
  const isLoadMore = useRef(false); // 是否正在加载更多
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [showLoadEnd, setShowLoadEnd] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const voiceAddModalRef = useRef<VoiceAddModalRef>(null);
  const lastAudio = useRef<AudioPlayerIns | null>(null);

  useEffect(() => {
    page = 0;
    loadList({});
  }, []);

  const handleOpenVoiceAddModal = () => {
    voiceAddModalRef.current?.open();
  };

  const loadListFn = (prop: {
    handle?: () => void;
    withoutLoading?: boolean;
  }) => {
    if (!isLoadMore.current && !prop.withoutLoading) setIsLoading(true);
    getVoiceList(page)
      .then((res) => {
        if (!res.empty) {
          const formatData = res.data.map((voiceItem) => {
            const avatarInfo = getSpeakerAvatarInfo(voiceItem.avatar);
            return Object.assign(voiceItem, avatarInfo);
          });
          setList(page === 1 ? formatData : list.concat(formatData));
          if (res.pageIndex >= res.totalPages) {
            setShowLoadEnd(true);
            console.log("当前页数==总页数");
          }
        }
        if (!isLoadMore.current && !prop.withoutLoading) setIsLoading(false);
        prop.handle?.();
      })
      .catch((e) => {
        console.log("列表加载错误", e);
      });
  };

  const loadList = (prop: {
    handle?: () => void;
    withoutLoading?: boolean;
  }) => {
    if (page === 0) {
      setList([]);
      setShowLoadEnd(false);
    }
    page++;
    loadListFn(prop);
  };

  const deleteCallback = (index: number) => {
    setList(list.filter((_, idx) => idx != index));
  };

  const handleCallback = (model: IVoiceItem, index: number) => {
    list[index] = model;
    console.log("handleCallback", model, index);
    setList(list);
  };

  const addCallback = () => {
    page = 0;
    loadList({ withoutLoading: true });
  };

  const onListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop; // 距顶部
    const clientHeight = e.currentTarget.clientHeight; // 可视区高度
    const scrollHeight = e.currentTarget.scrollHeight; // 滚动条总高度
    if (!isLoadMore.current && scrollTop + clientHeight + 40 >= scrollHeight) {
      if (showLoadEnd) {
        console.log("已经加载完毕，无需再次加载");
      } else {
        isLoadMore.current = true;
        setShowLoadMore(true);
        loadList({
          handle: () => {
            isLoadMore.current = false;
            setShowLoadMore(false);
          },
        });
      }
    }
  };

  const onAudioChange = (currentAudio: AudioPlayerIns) => {
    console.log("onAudioChange", currentAudio !== lastAudio.current);
    if (lastAudio.current && currentAudio !== lastAudio.current) {
      lastAudio.current.setCurrentTime(0);
      lastAudio.current.pause();
    }
    lastAudio.current = currentAudio;
  };

  return (
    <div className="w-full h-full flex flex-col select-text">
      <Header
        title="发言人声纹库"
        tip="管理发言人声纹信息，在会议过程中自动识别TA的声音"
        rightSlot={
          list.length ? (
            <div
              className="min-w-24.5 h-9.5 cursor-pointer bg-#383B50 rounded-2.5 flex items-center justify-center"
              onClick={handleOpenVoiceAddModal}
            >
              <IC_ADD className="w-4.5 h-4.5" />
              <span className="ml-1 text-#fff text-3.5">{"添加"}</span>
            </div>
          ) : null
        }
      />
      <div className="h-[calc(100%-4rem)] flex-1 flex flex-col items-center overflow-hidden">
        {isLoading && (
          <div className="flex-1 flex flex-items-center flex-justify-center">
            <Loading />
          </div>
        )}
        {!isLoading &&
          (list.length ? (
            <div
              className="w-full flex flex-col flex-1 pt-2 overflow-y-auto px-6"
              onScroll={(e) => {
                onListScroll(e);
              }}
            >
              <div className="w-full flex flex-wrap gap-4 mb-4">
                {list.map((voiceItem, index) => (
                  <VoiceCard
                    key={voiceItem.id}
                    voiceItem={voiceItem}
                    handleCallback={(model) => {
                      handleCallback(model, index);
                    }}
                    deleteCallback={() => deleteCallback(index)}
                    onAudioChange={onAudioChange}
                  />
                ))}
              </div>
              {showLoadMore && <LoadMore />}
            </div>
          ) : (
            <div className="w-full flex flex-1 mt--16">
              <div className="flex flex-col flex-justify-center flex-items-center flex-1">
                <img src={IC_NONE} className="w-20 h-20" />
                <span className="mt-1 text-mx-quaternary text-3.5">
                  {"暂无声纹记录"}
                </span>
                <div
                  className="min-w-24.5 h-9.5 cursor-pointer mt-3 bg-#4981f2 rounded-2.5 flex items-center justify-center"
                  onClick={handleOpenVoiceAddModal}
                >
                  <IC_ADD className="w-4.5 h-4.5" />
                  <span className="ml-1 text-#fff text-3.5">{"添加"}</span>
                </div>
              </div>
            </div>
          ))}
      </div>
      <VoiceAddModal ref={voiceAddModalRef} handleCallback={addCallback} />
    </div>
  );
};

export default Voice;
